// DERIVADO DE: gestion-web-cliente@a2aee525a
//   src/app/modulos/entidades/eventos-genericos/listado-eventos-genericos-historico/
//   eventos-genericos-historico.component.ts → `tipoEventoOpciones`
// CAMBIOS: sólo las tres opciones que IRIX habilita con el módulo Luminarias.

import type { PermisosService } from '@core/permisos/permisos.service';

/**
 * Qué eventos son "de alumbrado".
 *
 * **Son tres tipos, no uno.** IRIX habilita el filtro de tipo de evento por
 * módulo, y con el de Luminarias ofrece `Evento Luminaria`, `Evento Gateway` y
 * `Evento BotonBLE`: los gateways y los botones son la infraestructura del
 * alumbrado, y en FARO tienen su propia pantalla en Red LoRaWAN. Mirar sólo
 * `Evento Luminaria` dejaba afuera, por ejemplo, un gateway caído, que es lo
 * primero que explica por qué 300 luminarias dejaron de comunicar.
 *
 * Vive en `compartido/` porque lo usan la vista de eventos y el badge del menú,
 * y los dos tienen que contar lo mismo.
 */
export const TIPOS_EVENTO_ALUMBRADO = [
  'Evento Luminaria',
  'Evento Gateway',
  'Evento BotonBLE',
] as const;

export type TipoEventoAlumbrado = (typeof TIPOS_EVENTO_ALUMBRADO)[number];

/** Cómo se le dice a cada tipo en pantalla. */
export const ETIQUETA_TIPO_EVENTO: Record<TipoEventoAlumbrado, string> = {
  'Evento Luminaria': 'Luminaria',
  'Evento Gateway': 'Gateway',
  'Evento BotonBLE': 'Botón BLE',
};

/**
 * El filtro base de todo listado de eventos de FARO.
 *
 * `filtrador: 'Evento'` deja fuera los técnicos (que son solicitudes de
 * servicio técnico y tienen su pantalla) y los de emergencias.
 */
export const ALCANCE_EVENTOS = {
  filtrador: 'Evento',
  tipoEvento: { $in: [...TIPOS_EVENTO_ALUMBRADO] },
} as const;

/**
 * Los tipos de evento que el operador puede ver.
 *
 * Los de botón BLE sólo con `Administración - Ver botones Ble`: sin ese permiso
 * la pantalla de botones no existe para él, y tampoco sus eventos. Los de
 * gateway se dejan, como en IRIX: un gateway caído explica por qué un barrio
 * entero dejó de comunicar, aunque el operador no administre gateways.
 */
export function tiposEventoVisibles(permisos: PermisosService): readonly TipoEventoAlumbrado[] {
  const verBotones = permisos.puedeVer('botonesBle');
  return TIPOS_EVENTO_ALUMBRADO.filter((t) => t !== 'Evento BotonBLE' || verBotones);
}

/** `ALCANCE_EVENTOS` restringido a los tipos que se pueden ver. */
export function alcanceEventos(tipos: readonly TipoEventoAlumbrado[] = TIPOS_EVENTO_ALUMBRADO) {
  return { filtrador: 'Evento', tipoEvento: { $in: [...tipos] } };
}

/**
 * La rama de horarios de atención.
 *
 * Un evento sólo entra al listado vivo si el cliente que mira lo tiene dentro de
 * su horario de atención. Es lo que evita que el operador del turno mañana vea
 * los eventos que atiende el turno noche.
 *
 * **Las dos ramas de cruce de medianoche son necesarias.** Un rango 22:00–06:00
 * no cumple `horaInicio <= ahora < horaFin` en ningún momento; hay que partirlo
 * en "estamos después del inicio" (noche) y "estamos antes del fin" (madrugada),
 * y por eso la rama que se agrega depende de si es AM o PM.
 *
 * Vive acá porque la usan el listado vivo y el aviso sonoro de eventos nuevos,
 * que tienen que ver exactamente los mismos eventos.
 */
export function ramaHorarios(idCliente: string, ahora = new Date()): unknown {
  const diaActual = ahora.getDay();
  const hora = `${String(ahora.getHours()).padStart(2, '0')}:${String(ahora.getMinutes()).padStart(2, '0')}`;
  const esAM = ahora.getHours() < 12;

  return {
    $or: [
      // Sin horarios configurados: se ve siempre.
      { configHorariosAtencion: { $eq: [] } },
      {
        configHorariosAtencion: {
          $elemMatch: {
            idCliente,
            $and: [
              {
                $or: [{ dias: { $exists: false } }, { dias: { $size: 0 } }, { dias: diaActual }],
              },
              {
                $or: [
                  { horaInicio: { $exists: false } },
                  { horaFin: { $exists: false } },
                  { horaInicio: { $lte: hora }, horaFin: { $gt: hora } },
                  ...(esAM ? [{ horaInicio: { $gte: '12:00' }, horaFin: { $gt: hora } }] : []),
                  ...(esAM ? [] : [{ horaInicio: { $lte: hora }, horaFin: { $lte: '12:00' } }]),
                ],
              },
            ],
          },
        },
      },
    ],
  };
}

/**
 * Los parámetros de toda consulta de eventos **abiertos** (`requiereAtencion`)
 * que no sea el listado vivo: el badge del menú, las fichas del Panorama, el
 * turno y el informe diario.
 *
 * **Sin `vivo: 'true'` la consulta se cae.** Sin la bandera, el backend acota
 * por cliente con un `$or` de `idCliente`/`idsAncestros` que ningún índice
 * resuelve: barre los eventos abiertos de todos los clientes y Mongo corta por
 * tiempo (500 con `i/o timeout`, medido en prod el 2026-10-05). Con la bandera
 * acota por `idsClientesQueVen`, que entra por el índice parcial del vivo.
 *
 * `idsClientesQueVen` ya incluye a los clientes hijos (lleva los ancestros del
 * evento), así que `includeChildren` no cambia nada por ese camino. Con
 * `hijos` apagado se acota a mano a `idCliente`: si no, el interruptor
 * «Incluir clientes hijos» dejaría de tener efecto.
 */
export function consultaAbiertos(
  filtro: Record<string, unknown>,
  alcance: { hijos: boolean; idCliente?: string },
): { filter: string; vivo: 'true' } {
  const propio = !alcance.hijos && alcance.idCliente ? { idCliente: alcance.idCliente } : {};
  return {
    filter: JSON.stringify({ ...filtro, ...propio, requiereAtencion: true }),
    vivo: 'true',
  };
}
